import { ref } from 'vue';

import { useMetricDetails } from '@/app/dashboards/composables/useMetricDetails';
import { REPORTS } from '@/app/dashboards/utils/reports.enum';
import { MEDIA_TYPES } from '@/models/dashboards/media-types.enum';
import { useMetricsStore } from '@/stores/metrics';
import { getMetricDetails } from '@/utils/metrics';

/*
  Removes media type from metric name
 */
export const getMediaTypeMetricParentName = (metricName, channels, reportType) => {
  if (!metricName) return metricName;

  const { targetMetricDetails } = useMetricDetails({
    metric: ref(metricName),
    channels: ref(channels),
    metricReportTypeKey: ref(REPORTS?.[reportType]?.metricTypeReportKey),
  });
  return targetMetricDetails.value?.parent_metric ?? metricName;
};

export function getMediaTypeDetailsFromMetricKeys(
  metricKeys,
  channels,
  metricTypeReportKey,
  filterStrictLockedMetric = false,
) {
  /**
   *
   * @param  {boolean} filterStrictLockedMetric  if true, remove 'All Media' option if it's locked
   * to a specific media type. E.g. Instagram Shares is locked to Reels.
   *
   */
  let lockedMetric = false;
  const mediaTypeMetrics = metricKeys.reduce((acc, metricKey) => {
    const metricDetails = getMetricDetails(metricKey, channels, metricTypeReportKey);
    if (metricDetails.lockedMediaBreakdown) lockedMetric = true;
    const mediaTypeDetails = Object.values(MEDIA_TYPES).filter(
      (mediaTypeInfo) => mediaTypeInfo.text === metricDetails.mediaTypeLabel,
    )[0];
    acc[mediaTypeDetails.value] = {
      metric: metricKey,
      ...mediaTypeDetails,
    };
    return acc;
  }, {});

  if (lockedMetric && filterStrictLockedMetric && Object.keys(mediaTypeMetrics).length > 1) {
    delete mediaTypeMetrics[MEDIA_TYPES.ALL_MEDIA.value];
  }

  return mediaTypeMetrics;
}

export function getMediaTypeOptionsObjectFromMetricDetails(
  selectedMetricDetails,
  channel,
  metricTypeReportKey,
  filterStrictLockedMetric = false,
  includeHidden = false,
) {
  const metricsStore = useMetricsStore();
  if (!selectedMetricDetails.supportsMediaBreakdown) return {};
  const parentMetric = selectedMetricDetails.parentMetric;
  const childMetricKeys = metricsStore.getMetricKeysByParent(
    channel,
    metricTypeReportKey,
    parentMetric,
    includeHidden,
  );
  return getMediaTypeDetailsFromMetricKeys(
    childMetricKeys,
    [channel],
    metricTypeReportKey,
    filterStrictLockedMetric,
  );
}

export function resetOrLockMediaTypeFromMetricDetails(metricDetails, mediaTypeOptions) {
  if (metricDetails?.lockedMediaBreakdown) {
    if (mediaTypeOptions.length >= 1) {
      return mediaTypeOptions.reduce((acc, mediaTypeInfo) => {
        if (mediaTypeInfo.value !== MEDIA_TYPES.ALL_MEDIA.value) {
          acc.push(mediaTypeInfo.value);
        }
        return acc;
      }, []);
    }
  }
  return [];
}

export const getMetricMediaType = (channel, metric, reportType = null, includeHidden = false) => {
  const metricDetails = getMetricDetails(metric, [channel], reportType);
  const mediaTypeMetrics = getMediaTypeOptionsObjectFromMetricDetails(
    metricDetails,
    channel,
    reportType,
    true,
    includeHidden,
  );
  return Object.values(mediaTypeMetrics).find((mediaSubType) => mediaSubType.metric === metric);
};

export const getMediaTypesBasedOnMetrics = (selectedMetricObject, mediaTypes) => {
  if (!selectedMetricObject?.mediaSubtypes) {
    return [];
  }

  if (selectedMetricObject?.mediaSubtypeLocked || !mediaTypes) {
    return [Object.keys(selectedMetricObject.mediaSubtypes)[0]];
  }

  return mediaTypes.filter((mediaType) =>
    Object.keys(selectedMetricObject.mediaSubtypes).includes(mediaType),
  );
};

export const getMetricsBasedOnMediaTypes = (
  selectedMetric,
  selectedMetricObject,
  mediaTypes = [],
  channel = null,
  reportKey = null,
) => {
  if (mediaTypes.length === 0) {
    return selectedMetric ? [selectedMetric] : [];
  }

  const metricDetails = getMetricDetails(selectedMetric, [channel], reportKey);
  const mediaTypeMetrics = getMediaTypeOptionsObjectFromMetricDetails(
    metricDetails,
    channel,
    reportKey,
    true,
  );
  return mediaTypes.map((type) => mediaTypeMetrics?.[type]?.metric);
};

export const reorderSelectedMediaTypes = (selectedMediaTypes) =>
  Object.keys(MEDIA_TYPES).filter((mediaType) => selectedMediaTypes.includes(mediaType));

function getSelectedMediaTypeObject(reportMetricDetails) {
  const mediaType = Object.values(MEDIA_TYPES).find(
    (mediaTypeData) => mediaTypeData.text === reportMetricDetails.mediaTypeLabel,
  );
  return mediaType ?? {};
}

export function getContentReportMediaTypeMetric(
  reportMetricDetails,
  reportChannel,
  contentMetricDetails,
  metricTypeReportKey,
) {
  const selectedMediaTypeObject = getSelectedMediaTypeObject(reportMetricDetails);
  const mediaTypeOptionsForContentMetric = getMediaTypeOptionsObjectFromMetricDetails(
    contentMetricDetails,
    reportChannel,
    metricTypeReportKey,
  );
  const mediaTypeMetricData = Object.values(mediaTypeOptionsForContentMetric);
  if (contentMetricDetails.lockedMediaBreakdown) {
    if (mediaTypeMetricData.length > 1) {
      const metricKeyList = mediaTypeMetricData.reduce((acc, mediaTypeData) => {
        if (mediaTypeData.value !== MEDIA_TYPES.ALL_MEDIA.value) {
          acc.push(mediaTypeData.metric);
        }
        return acc;
      }, []);
      return metricKeyList[0];
    }
    return mediaTypeMetricData?.[0]?.metric;
  }

  return mediaTypeOptionsForContentMetric?.[selectedMediaTypeObject?.value]?.metric;
}
